import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PlusIcon, SteeringWheelIcon, KeyIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon, CopyIcon, ArrowClockwiseIcon, UploadIcon, PaperPlaneTiltIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import BulkImportDialog from "@/components/BulkImportDialog";

const empty = { name: "", license_number: "", phone: "", email: "", notes: "" };

export default function Drivers() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loginFor, setLoginFor] = useState(null); // driver whose login we're creating
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [resetFor, setResetFor] = useState(null); // driver whose password we're resetting
  const [resetPassword, setResetPassword] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [shownPasswords, setShownPasswords] = useState({}); // {driverId: bool}

  const load = async () => {
    const [d, t] = await Promise.all([api.get("/drivers"), api.get("/trucks")]);
    setDrivers(d.data);
    setTrucks(t.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e?.detail?.entity === "drivers") load(); };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
  }, []);

  const truckCountFor = (id) => trucks.filter((t) => t.driver_id === id).length;

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/drivers/${editing.id}`, form);
      else await api.post("/drivers", form);
      toast.success(editing ? "Driver updated" : "Driver added");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (d) => {
    if (!window.confirm("Delete this driver?")) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const openCreateLogin = (d) => {
    setLoginFor(d);
    setLoginForm({ email: d.email || "", password: "" });
  };

  const saveLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/drivers/${loginFor.id}/create-login`, loginForm);
      toast.success(`Login created for ${loginFor.name}. Share credentials with them.`);
      setLoginFor(null);
      setLoginForm({ email: "", password: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const removeLogin = async (d) => {
    if (!window.confirm(`Remove login access for ${d.name}? Their account will be deleted.`)) return;
    try {
      await api.delete(`/drivers/${d.id}/login`);
      toast.success("Login removed");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const openReset = (d) => {
    setResetFor(d);
    setResetPassword("");
  };

  const saveReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/drivers/${resetFor.id}/reset-password`, { password: resetPassword });
      toast.success(`Password reset for ${resetFor.name}. Share the new password with them.`);
      setResetFor(null);
      setResetPassword("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const togglePassword = (id) => setShownPasswords((s) => ({ ...s, [id]: !s[id] }));

  const sendLogin = async (d) => {
    if (!d.phone) { toast.error("Add a phone number to this driver first"); return; }
    try {
      const { data } = await api.post(`/drivers/${d.id}/send-login`);
      toast.success(`Login texted to ${d.name} at ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send SMS");
    }
  };

  return (
    <div data-testid="drivers-page">
      <PageHeader eyebrow="Roster" title="Drivers" subtitle="Manage drivers and assignments across the fleet.">
        {user?.role === "admin" && (
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-sm" data-testid="bulk-import-drivers-button">
            <UploadIcon size={16} weight="bold" className="mr-2" /> Bulk import
          </Button>
        )}
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-driver-button">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Driver
        </Button>
      </PageHeader>

      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={load}
        kind="drivers"
        title="drivers"
        columnList="NAME · LICENSE NUMBER · PHONE · EMAIL"
      />

      <div className="p-8">
        {drivers.length === 0 ? (
          <div className="bg-white border border-slate-300 rounded-sm p-12 text-center">
            <SteeringWheelIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-600">No drivers added yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drivers.map((d) => (
              <div key={d.id} className="bg-white border border-slate-300 rounded-sm p-5" data-testid={`driver-card-${d.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center text-white font-display font-black text-lg">
                    {d.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold tracking-tight truncate">{d.name}</div>
                    <div className="font-mono text-xs text-slate-500">{d.license_number}</div>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  {d.phone && <div className="text-slate-700"><span className="font-mono text-[10px] uppercase text-slate-400 mr-2">Phone</span>{d.phone}</div>}
                  {d.email && <div className="text-slate-700 truncate"><span className="font-mono text-[10px] uppercase text-slate-400 mr-2">Email</span>{d.email}</div>}
                  <div className="text-slate-700"><span className="font-mono text-[10px] uppercase text-slate-400 mr-2">Trucks</span>{truckCountFor(d.id)} assigned</div>
                  <div className="text-slate-700 flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase text-slate-400">Login</span>
                    {d.user_id ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-mono text-[11px]">
                        <CheckCircleIcon size={12} weight="fill" /> {d.login_email || "active"}
                      </span>
                    ) : (
                      <span className="text-slate-500 font-mono text-[11px]">no login</span>
                    )}
                  </div>
                  {d.user_id && d.login_password && (
                    <div className="text-slate-700 flex items-center gap-2" data-testid={`driver-password-row-${d.id}`}>
                      <span className="font-mono text-[10px] uppercase text-slate-400">Password</span>
                      <span className="font-mono text-[12px] text-slate-800 select-all" data-testid={`driver-password-${d.id}`}>
                        {shownPasswords[d.id] ? d.login_password : "•".repeat(Math.min(d.login_password.length, 10))}
                      </span>
                      <button
                        type="button"
                        onClick={() => togglePassword(d.id)}
                        className="text-slate-400 hover:text-slate-700"
                        title={shownPasswords[d.id] ? "Hide password" : "Show password"}
                        data-testid={`toggle-password-${d.id}`}
                      >
                        {shownPasswords[d.id] ? <EyeSlashIcon size={14} weight="bold" /> : <EyeIcon size={14} weight="bold" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyText(d.login_password, "Password")}
                        className="text-slate-400 hover:text-blue-600"
                        title="Copy password"
                        data-testid={`copy-password-${d.id}`}
                      >
                        <CopyIcon size={14} weight="bold" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end gap-3 text-xs">
                  {!d.user_id ? (
                    <button onClick={() => openCreateLogin(d)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold" data-testid={`create-login-${d.id}`}>
                      <KeyIcon size={12} weight="bold" /> Create Login
                    </button>
                  ) : (
                    <>
                      <button onClick={() => sendLogin(d)} disabled={!d.phone} title={d.phone ? "Text login to driver" : "Add a phone number first"} className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-semibold disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`send-login-${d.id}`}>
                        <PaperPlaneTiltIcon size={12} weight="bold" /> Send Login (SMS)
                      </button>
                      <button onClick={() => openReset(d)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold" data-testid={`reset-password-${d.id}`}>
                        <ArrowClockwiseIcon size={12} weight="bold" /> Reset Password
                      </button>
                      <button onClick={() => removeLogin(d)} className="text-red-600 hover:text-red-700 font-semibold" data-testid={`remove-login-${d.id}`}>
                        Remove Login
                      </button>
                    </>
                  )}
                  <button onClick={() => { setEditing(d); setForm({ ...empty, ...d }); setOpen(true); }} className="text-blue-600 hover:underline font-semibold" data-testid={`edit-driver-${d.id}`}>Edit</button>
                  {user?.role === "admin" && (
                    <button onClick={() => del(d)} className="text-red-600 hover:underline font-semibold" data-testid={`del-driver-${d.id}`}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit driver" : "Add driver"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="driver-name" />
            <Field label="License Number *" required value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} tid="driver-license" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="driver-phone" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="driver-email" />
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="driver-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="driver-save">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREATE LOGIN DIALOG */}
      <Dialog open={!!loginFor} onOpenChange={(v) => { if (!v) setLoginFor(null); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="driver-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Create login for {loginFor?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveLogin} className="space-y-4">
            <div className="text-xs text-slate-500 border border-slate-300 bg-slate-50 rounded-sm p-3">
              After saving, share the email + password with the driver. They can then sign in at the same URL and use their Driver Portal to see their truck, services, and report issues.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Email *</Label>
              <Input type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="mt-1.5 rounded-sm font-mono" data-testid="driver-login-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Password *</Label>
              <Input type="text" required minLength={4} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="min 4 characters — share with driver" className="mt-1.5 rounded-sm font-mono" data-testid="driver-login-password" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLoginFor(null)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="driver-login-save">Create Login</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={!!resetFor} onOpenChange={(v) => { if (!v) { setResetFor(null); setResetPassword(""); } }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="driver-reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Reset password for {resetFor?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveReset} className="space-y-4">
            <div className="text-xs text-slate-500 border border-slate-300 bg-slate-50 rounded-sm p-3">
              This will overwrite the driver's current password. The new password is stored so you can view it on this page anytime.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Login Email</Label>
              <Input type="text" value={resetFor?.login_email || ""} disabled className="mt-1.5 rounded-sm font-mono bg-slate-50" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">New Password *</Label>
              <Input type="text" required minLength={4} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="min 4 characters" className="mt-1.5 rounded-sm font-mono" data-testid="driver-reset-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetFor(null)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="driver-reset-password-save">Reset Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5 rounded-sm" data-testid={tid} />
    </div>
  );
}
